import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import dayjs from 'dayjs';
import DropdownFilter from '../search/DropdownFilter';
import Dropdown from '../formControl/Dropdown';
import { motion } from 'motion/react';
import { accordionPanel } from '@/animations';
import classNames from 'classnames';
import EventHierarchicalDateFilter from './EventHierarchicalDateFilter';

type FilterOptionType = {
  label: string;
  value: string;
  count?: number;
  disabled?: boolean;
};

type DateFilterType = {
  label: string;
  id: string;
  children?: Array<{ label: string; id: string }>;
};

type FiltersType = {
  eventTypes: string[];
  directories: FilterOptionType[];
  date: DateFilterType[];
};

type ChangeFiltersType = {
  date: string[];
  directory: string[];
  event: string;
};

interface EventCalendarFiltersProps {
  filters: FiltersType;
  onFilterChange: (filters: {
    eventDateFilter: string[];
    eventTypeFilter: string | null;
    directoryFilter: string[] | null;
  }) => void;
}

const EventCalendarFilters = ({ filters, onFilterChange }: EventCalendarFiltersProps) => {
  const t = useTranslations();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [changeFilters, setChangeFilters] = useState<ChangeFiltersType>({
    date: [],
    directory: [],
    event: '',
  });
  const [dateLabel, setDateLabel] = useState('Date');

  const getMonthsFromYear = useCallback((date: string) => {
    const months = [];
    for (let i = 1; i <= 12; i++) {
      months.push(dayjs(date).format('YYYY') + '-' + i.toString().padStart(2, '0') + '-01T00:00:00');
    }
    return months;
  }, []);

  const updateFilters = useCallback(() => {
    if (changeFilters.date && changeFilters.date.length > 0) {
      let count = 0;
      filters.date.forEach((year) => {
        if (year.children) {
          const allMonthIds = year.children.map((m) => m.id);
          const selectedMonthsInYear = changeFilters.date.filter((d) => allMonthIds.includes(d));
          if (selectedMonthsInYear.length === year.children.length) {
            count += 1;
          } else {
            count += selectedMonthsInYear.length;
          }
        }
      });
      setDateLabel(`Date (${count})`);
    } else {
      setDateLabel('Date');
    }

    const selectedMonthYear =
      changeFilters.date.length > 0
        ? changeFilters.date[changeFilters.date.length - 1]
        : dayjs(new Date()).format('YYYY-MM-01');

    onFilterChange({
      eventDateFilter:
        changeFilters.date && changeFilters.date.length > 0 ? changeFilters.date : getMonthsFromYear(selectedMonthYear),
      eventTypeFilter: changeFilters.event || null,
      directoryFilter: changeFilters.directory && changeFilters.directory.length > 0 ? changeFilters.directory : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeFilters, filters.date, getMonthsFromYear]);

  const resetFilters = () => {
    setChangeFilters({
      date: [],
      directory: [],
      event: '',
    });
  };

  useEffect(() => {
    updateFilters();
  }, [updateFilters]);

  const handleDateChange = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setChangeFilters((prev) => ({
        ...prev,
        date: value,
      }));
    } else {
      setChangeFilters((prev) => ({
        ...prev,
        date: prev.date.includes(value) ? prev.date.filter((d) => d !== value) : [...prev.date, value],
      }));
    }
  };

  const handleDirectoryChange = (value: string) => {
    setChangeFilters((prev) => ({
      ...prev,
      directory: prev.directory.includes(value)
        ? prev.directory.filter((d) => d !== value)
        : [...prev.directory, value],
    }));
  };

  const handleEventTypeChange = (event: { target: { name: string; value: string | null } }) => {
    setChangeFilters((prev) => ({
      ...prev,
      event: event.target.value || '',
    }));
  };

  const filterComponents = (
    <div className="flex flex-col justify-start gap-2 border-b border-black/10 py-4 md:flex-row md:flex-wrap md:gap-6 md:border-b-0">
      <div className="w-auto">
        <EventHierarchicalDateFilter
          name={dateLabel}
          value={changeFilters.date}
          options={filters.date}
          onChange={handleDateChange}
          onClear={() => setChangeFilters((prev) => ({ ...prev, date: [] }))}
        />
      </div>
      {filters.directories && (
        <div className="w-auto">
          <DropdownFilter
            name={t('event.eventCalendarFilters.$directories')}
            value={changeFilters.directory}
            options={filters.directories.map((d) => ({
              ...d,
              count: d.count ?? 0,
              disabled: d.disabled ?? false,
            }))}
            disabled={false}
            onChange={handleDirectoryChange}
            onClear={() => setChangeFilters((prev) => ({ ...prev, directory: [] }))}
          />
        </div>
      )}
      {filters.eventTypes && (
        <div className="w-auto">
          <Dropdown
            name="eventType"
            value={changeFilters.event || undefined}
            placeholder="Event type"
            options={filters.eventTypes.map((type) => ({ label: type, value: type }))}
            onChange={handleEventTypeChange}
            onClear={() => setChangeFilters((prev) => ({ ...prev, event: '' }))}
            className={classNames(
              '[&_.content]:rounded-3xl [&_.content]:border [&_.content]:border-black/10 [&_.content]:shadow-lg',
              '[&_.content]:min-w-[125px]',
              '[&_.main-button]:h-10 [&_.main-button]:justify-center [&_.main-button]:rounded-4xl [&_.main-button]:border [&_.main-button]:border-black/10 [&_.main-button]:px-4 [&.open_.main-button]:bg-white',
              '[&_.popover-title]:text-sm [&_.popover-title]:font-medium',
              '[&_.option]:text-base [&_.option]:text-black',
            )}
            contentWrapperClassName="max-h-60 "
            offset={10}
            placement="bottom-start"
          />
        </div>
      )}
      <div className="flex items-center justify-center lg:justify-start">
        <button
          className="btn cursor-pointer text-sm font-semibold underline [--underline-width:100%]"
          onClick={resetFilters}
        >
          {t('search.$clearAll')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="py-md-0 sticky top-16 z-50 min-h-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="hidden md:flex">{filterComponents}</div>

        <button
          className="mt-3 h-11 w-full rounded-full border border-black/20 px-3 font-semibold md:hidden"
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
        >
          {t('general.$filters')}
        </button>

        <motion.div
          variants={accordionPanel.variants}
          initial="inactive"
          animate={mobileFiltersOpen ? 'active' : 'inactive'}
          className={classNames('md:hidden print:!h-auto print:!opacity-100', !mobileFiltersOpen && 'overflow-hidden')}
        >
          {filterComponents}
        </motion.div>
      </div>
    </div>
  );
};

export default EventCalendarFilters;
